import React, { useState } from "react";
import { BookOpen, Copy, Layers, ListOrdered, Scale, Table2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    AlertBox,
    DataTable,
    EmptyResult,
    IconTab,
    KpiCard,
    PanelHeader,
    tl,
} from "./AktueryaUiParts";
import { CetvelTable } from "./AktueryaFormPanels";

const RESULT_TABS = [
    { id: "ozet", label: "Özet", icon: Layers },
    { id: "adimlar", label: "Adımlar", icon: ListOrdered },
    { id: "cetvel", label: "Cetvel", icon: Table2 },
    { id: "yasal", label: "Yasal", icon: BookOpen },
];

export default function AktueryaSonucPanel({ sonuc, kanaat = "", onKanaatChange }) {
    const [view, setView] = useState("ozet");
    if (!sonuc) return <EmptyResult />;

    const ozet = sonuc.ozet || {};
    const kesinNet = sonuc.kesin_sonuc?.ozet?.net_tazminat ?? ozet.net_tazminat;

    function kopyala() {
        navigator.clipboard?.writeText(String(kesinNet ?? ""));
        toast.success("Kesin net tutar kopyalandı");
    }

    const karsilastirmaRows = [
        {
            _key: "kesin",
            senaryo: "Kesin Hesap",
            net: tl(sonuc.kesin_sonuc?.ozet?.net_tazminat ?? ozet.net_tazminat),
            kusur: tl(sonuc.kesin_sonuc?.ozet?.kusur_sonrasi ?? ozet.kusur_sonrasi),
        },
        ...["alt", "orta", "ust"].map((key) => {
            const b = sonuc.tahmini_sonuclar?.[key];
            if (!b) return null;
            return {
                _key: key,
                senaryo: b.etiket,
                net: tl(b.ozet?.net_tazminat),
                kusur: tl(b.ozet?.kusur_sonrasi),
            };
        }).filter(Boolean),
    ];

    return (
        <div className="rounded-xl border border-white/10 bg-slate-950/20 overflow-hidden">
            <PanelHeader
                title="Hesap Sonucu"
                action={
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 hidden sm:inline">
                            {sonuc.hesap_turu === "olumlu" ? "DYK" : "SİG"} · TRH-2010
                        </span>
                        <Button type="button" size="sm" variant="ghost" className="h-8" onClick={kopyala}>
                            <Copy className="h-3 w-3 mr-1" /> Kopyala
                        </Button>
                    </div>
                }
            />

            <div className="p-4 space-y-4">
                <AlertBox items={sonuc.uyarilar} />

                <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
                    <KpiCard label="Kesin Net" value={tl(kesinNet)} accent="gold" hint="Belgeli parametreler" />
                    <KpiCard
                        label="Tahmini Band"
                        value={
                            sonuc.tahmini_sonuclar?.aralik
                                ? `${tl(sonuc.tahmini_sonuclar.aralik.min)} – ${tl(sonuc.tahmini_sonuclar.aralik.max)}`
                                : "—"
                        }
                        accent="emerald"
                        hint={`Önerilen: ${tl(sonuc.tahmini_sonuclar?.aralik?.onerilen)}`}
                    />
                    {sonuc.hesap_turu === "yaralanmali" ? (
                        <>
                            <KpiCard label="Geçmiş Dönem" value={tl(ozet.gecmis_donem_toplam)} accent="sky" />
                            <KpiCard label="İşleyecek PV" value={tl(ozet.isleyecek_donem_toplam)} hint={`Brüt: ${tl(ozet.brut_toplam)}`} />
                            {ozet.gig_toplam > 0 ? <KpiCard label="GİG" value={tl(ozet.gig_toplam)} accent="emerald" /> : null}
                            {ozet.bakim_gideri_pv > 0 ? <KpiCard label="Bakım PV" value={tl(ozet.bakim_gideri_pv)} /> : null}
                        </>
                    ) : (
                        <>
                            <KpiCard label="Hak Sahibi" value={String(sonuc.hak_sahipleri?.length || 0)} />
                            <KpiCard label="Brüt Toplam" value={tl(ozet.brut_toplam)} hint={`Kusur sonrası: ${tl(ozet.kusur_sonrasi)}`} />
                        </>
                    )}
                </div>

                <IconTab tabs={RESULT_TABS} active={view} onChange={setView} />

                {view === "ozet" && (
                    <div className="space-y-4">
                        {sonuc.tahmini_sonuclar ? (
                            <div className="rounded-xl border border-white/10 overflow-hidden">
                                <PanelHeader title="Kesin vs Tahmini" />
                                <DataTable
                                    highlightFirst
                                    columns={[
                                        { key: "senaryo", label: "Senaryo" },
                                        { key: "net", label: "Net Tazminat", align: "right" },
                                        { key: "kusur", label: "Kusur Sonrası", align: "right" },
                                    ]}
                                    rows={karsilastirmaRows}
                                />
                            </div>
                        ) : null}

                        {sonuc.bilirkişi_kanaati || onKanaatChange ? (
                            <div className="rounded-xl border border-white/10 p-4 bg-slate-900/30 space-y-3">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
                                        <Scale className="h-3.5 w-3.5" /> Bilirkişi Kanaati
                                    </div>
                                    {sonuc.bilirkişi_kanaati_ozel ? (
                                        <span className="text-[10px] text-[#D4AF37]">Özel metin</span>
                                    ) : null}
                                </div>
                                {onKanaatChange ? (
                                    <textarea
                                        className="w-full min-h-[100px] rounded-lg border border-white/10 bg-slate-950/60 px-3 py-2 text-sm text-slate-300"
                                        value={kanaat}
                                        onChange={(e) => onKanaatChange(e.target.value)}
                                        placeholder={sonuc.bilirkişi_kanaati?.replace(/<[^>]+>/g, "") || "Otomatik kanaat — düzenleyebilirsiniz. Boş bırakırsanız sistem metni kullanılır."}
                                    />
                                ) : (
                                    <p className="text-sm text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: sonuc.bilirkişi_kanaati.replace(/<b>/g, '<strong class="text-white">').replace(/<\/b>/g, "</strong>") }} />
                                )}
                                {onKanaatChange && !kanaat.trim() && sonuc.bilirkişi_kanaati ? (
                                    <p className="text-xs text-slate-500 italic border-t border-white/5 pt-2" dangerouslySetInnerHTML={{ __html: `Otomatik: ${sonuc.bilirkişi_kanaati}` }} />
                                ) : null}
                            </div>
                        ) : null}

                        {sonuc.hesap_turu === "olumlu" && sonuc.hak_sahipleri?.length ? (
                            <div className="rounded-xl border border-white/10 overflow-hidden">
                                <PanelHeader title="Hak Sahibi Dağılımı" />
                                <DataTable
                                    columns={[
                                        { key: "ad", label: "Ad Soyad", render: (h) => h.ad_soyad },
                                        { key: "pay", label: "Pay", render: (h) => h.pay_adedi },
                                        { key: "net", label: "Net", align: "right", render: (h) => tl(h.net_tazminat) },
                                    ]}
                                    rows={sonuc.hak_sahipleri}
                                />
                            </div>
                        ) : null}

                        {sonuc.magdur ? (
                            <p className="text-xs text-slate-500 border-t border-white/5 pt-3">
                                Mağdur {sonuc.magdur.yas} yaş · bakiye ömür {sonuc.magdur.bakiye_omur} yıl · aktif {sonuc.magdur.aktif_yil} y · pasif {sonuc.magdur.pasif_yil} y
                            </p>
                        ) : null}
                        {sonuc.muteveffa ? (
                            <p className="text-xs text-slate-500 border-t border-white/5 pt-3">
                                Müteveffa {sonuc.muteveffa.yas} yaş · bakiye ömür {sonuc.muteveffa.bakiye_omur} yıl
                            </p>
                        ) : null}
                    </div>
                )}

                {view === "adimlar" && (
                    <div className="rounded-xl border border-white/10 overflow-hidden">
                        <PanelHeader title="Hesaplama Adımları — Yargıtay Progresif Rant" />
                        {sonuc.hesap_adimlari?.length ? (
                            <DataTable
                                columns={[
                                    { key: "adim", label: "#", render: (a) => a.adim },
                                    { key: "baslik", label: "Adım" },
                                    { key: "formul", label: "Formül / Girdi" },
                                    { key: "sonuc", label: "Sonuç", align: "right" },
                                ]}
                                rows={sonuc.hesap_adimlari}
                            />
                        ) : (
                            <p className="text-sm text-slate-500 p-6">Adım verisi yok — yeniden hesaplayın.</p>
                        )}
                    </div>
                )}

                {view === "cetvel" && (
                    <div className="rounded-xl border border-white/10 overflow-hidden">
                        <PanelHeader title={`Progresif Rant Cetveli (${(sonuc.progresif_rant_cetveli || []).length} yıl)`} />
                        <CetvelTable rows={sonuc.progresif_rant_cetveli} max={40} />
                        {(sonuc.progresif_rant_cetveli || []).length > 40 ? (
                            <p className="text-[10px] text-slate-500 px-4 py-2 border-t border-white/5">
                                +{(sonuc.progresif_rant_cetveli || []).length - 40} satır — PDF&apos;de tam liste
                            </p>
                        ) : null}
                    </div>
                )}

                {view === "yasal" && (
                    <div className="space-y-4">
                        <div className="rounded-xl border border-white/10 p-4">
                            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Yöntem Özeti</div>
                            <p className="text-sm text-slate-300 leading-relaxed">{sonuc.yontem_aciklama || "TRH-2010 + progresif rant"}</p>
                        </div>
                        <div className="rounded-xl border border-white/10 overflow-hidden">
                            <PanelHeader title="Yasal Dayanak" />
                            <ul className="p-4 space-y-2">
                                {(sonuc.yasal_dayanak || []).map((ref, i) => (
                                    <li key={i} className="text-sm text-slate-400 flex gap-2">
                                        <span className="text-slate-600 shrink-0">{i + 1}.</span>
                                        {ref}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {(sonuc.tahmini_sonuclar?.notlar || []).map((n, i) => (
                            <p key={i} className="text-xs text-slate-500 italic">• {n}</p>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
